import { useAuth } from "./auth";
import { API_URL } from "./constants";
import { Logger } from "./logger";

enum HTTP_STATUS {
  OK = 200,
  NO_CONTENT = 204,
}

const isPDF = (response: Response) => {
  const contentType = response.headers.get("Content-Type");
  return !!contentType && contentType.includes("application/pdf");
};

const isEmptyBodyStatus = (status: number) => {
  return status === HTTP_STATUS.NO_CONTENT;
};

const useApiClient = () => {
  const { getAccessToken } = useAuth();

  const getHeaders = async () => {
    let token;
    try {
      token = await getAccessToken();
    } catch (error) {
      Logger.error(error);
      token = null;
    }

    const headersConfig = {
      "Content-Type": "application/json",
      "X-Client": "Employee-Web-App",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    return new Headers(headersConfig);
  };

  return {
    get: async (url: string) => {
      const response = await fetch(`${API_URL}${url}`, {
        headers: await getHeaders(),
      });

      if (!response.ok) {
        throw new Error();
      }

      if (isEmptyBodyStatus(response.status)) {
        return {
          data: null,
          status: response.status,
        };
      }

      if (isPDF(response)) {
        return {
          data: await response.blob(),
          status: response.status,
        };
      }

      return {
        data: await response.json(),
        status: response.status,
      };
    },
    post: async (url: string, body?: object) => {
      const response = await fetch(`${API_URL}${url}`, {
        method: "POST",
        ...(body && { body: JSON.stringify(body) }),
        headers: await getHeaders(),
      });

      if (!response.ok) {
        throw new Error();
      }

      if (isEmptyBodyStatus(response.status)) {
        return {
          data: null,
          status: response.status,
        };
      }

      try {
        return {
          data: await response.json(),
          status: response.status,
        };
      } catch (error) {
        return {
          data: null,
          status: response.status,
        };
      }
    },
    put: async (url: string, body?: object) => {
      const response = await fetch(`${API_URL}${url}`, {
        method: "PUT",
        ...(body && { body: JSON.stringify(body) }),
        headers: await getHeaders(),
      });

      if (!response.ok) {
        throw new Error();
      }
    },
  };
};

export { useApiClient, HTTP_STATUS };
